import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { statusService, type UserStatus, type UserStatusInfo } from '@/services/statusService';
import { sessionService } from '@/services/sessionService';

export interface UseOnlineStatusOptions {
  autoTrack?: boolean; // Whether to automatically track tab activity
  initialStatus?: UserStatus;
}

export interface UseOnlineStatusReturn {
  currentStatus: UserStatus;
  isOnline: boolean;
  updateStatus: (status: UserStatus) => Promise<void>;
  getUserStatus: (userId: string) => Promise<UserStatusInfo | null>;
  getUsersStatuses: (userIds: string[]) => Promise<UserStatusInfo[]>;
  lastSeen: string | null;
  isTracking: boolean;
}

export const useOnlineStatus = (options: UseOnlineStatusOptions = {}): UseOnlineStatusReturn => {
  const { autoTrack = true, initialStatus = 'online' } = options;
  const { user } = useAuth();
  const [currentStatus, setCurrentStatus] = useState<UserStatus>(initialStatus);
  const [isOnline, setIsOnline] = useState(true);
  const [lastSeen, setLastSeen] = useState<string | null>(null);
  const [isTracking, setIsTracking] = useState(false);

  const cleanupRef = useRef<(() => void) | null>(null);
  const statusListenerRef = useRef<(() => void) | null>(null);

  // Initialize status tracking when user is available
  useEffect(() => {
    if (!user?.id || !autoTrack) return;

    let mounted = true;

    const initializeStatus = async () => {
      try {
        setIsTracking(true);

        // First, check if user already has a saved status
        const existingStatus = await statusService.getUserStatus(user.id);

        // Initialize local state with existing status or default
        if (existingStatus && mounted) {
          setCurrentStatus(existingStatus.status);
          setIsOnline(existingStatus.is_online || false);
          setLastSeen(existingStatus.last_seen);
        }

        // Set up real-time listener for status changes AFTER setting initial state
        statusListenerRef.current = statusService.onStatusUpdate(user.id, (statusInfo) => {
          setCurrentStatus(statusInfo.status);
          setIsOnline(statusInfo.is_online || false);
          setLastSeen(statusInfo.last_seen);
        });

        if (existingStatus) {
          // Check if we have a preferred status stored in the database
          const preferredStatus = await statusService.getPreferredStatus(user.id);

          if (preferredStatus && ['online', 'away', 'busy', 'invisible'].includes(preferredStatus)) {
            // User has a preferred status - try to restore it regardless of current status
            try {
              // Set manual status to prevent automatic overrides during restoration
              statusService.setManualStatus(preferredStatus);

              const updateResult = await statusService.updateStatus(preferredStatus, false, true, user.id);

              if (!updateResult) {
                throw new Error('Status update returned false - likely offline or queued');
              }

              // Wait for the update to complete and verify with retries
              let verified = false;
              let attempts = 0;
              const maxAttempts = 5;

              while (!verified && attempts < maxAttempts) {
                attempts++;
                const delay = attempts * 200; // 200ms, 400ms, 600ms, 800ms, 1000ms
                await new Promise(resolve => setTimeout(resolve, delay));

                try {
                  const verifiedStatus = await statusService.getUserStatus(user.id);
                  if (verifiedStatus && verifiedStatus.status === preferredStatus) {
                    verified = true;
                  }
                } catch (error) {
                  // Verification failed, will retry
                }
              }

              // Initialize user tracking with heartbeat
              await statusService.initializeUser(user.id, false);
            } catch (error) {
              console.error('Failed to restore preferred status:', error);
              // On error, still initialize tracking
              await statusService.initializeUser(user.id, false);
            }
          } else {
            // No preferred status to restore - current status is already set above
            // If status is 'offline' and page is visible/focused, it should automatically go online via visibility tracking
            await statusService.initializeUser(user.id, false);
          }
        } else {
          // No existing status in database - check for preferred status first
          const preferredStatus = await statusService.getPreferredStatus(user.id);

          if (preferredStatus && ['online', 'away', 'busy', 'invisible'].includes(preferredStatus)) {
            // Restore preferred status even if no existing status in database
            try {
              // Set initial state to preferred status
              if (mounted) {
                setCurrentStatus(preferredStatus);
                setIsOnline(preferredStatus !== 'invisible');
                setLastSeen(new Date().toISOString());
              }

              // Set manual status to prevent automatic overrides during restoration
              statusService.setManualStatus(preferredStatus);

              await statusService.updateStatus(preferredStatus, false, true, user.id);

              // Wait for the update to complete and verify with retries
              let verified = false;
              let attempts = 0;
              const maxAttempts = 5;

              while (!verified && attempts < maxAttempts) {
                attempts++;
                const delay = attempts * 200; // 200ms, 400ms, 600ms, 800ms, 1000ms
                await new Promise(resolve => setTimeout(resolve, delay));

                try {
                  const verifiedStatus = await statusService.getUserStatus(user.id);
                  if (verifiedStatus && verifiedStatus.status === preferredStatus) {
                    console.log(`Status restoration confirmed via database check (attempt ${attempts})`);
                    verified = true;
                  }
                } catch (error) {
                  console.warn(`Database check failed on attempt ${attempts}:`, error);
                }
              }

              if (!verified) {
                console.warn('Status restoration failed verification after all attempts, but local state was set');
              }

              // Initialize user tracking
              await statusService.initializeUser(user.id, false);
            } catch (error) {
              console.error('Failed to restore preferred status:', error);
              // Fall back to normal initialization
              await statusService.initializeUser(user.id, true);
              if (mounted) {
                setCurrentStatus('online');
                setIsOnline(true);
                setLastSeen(new Date().toISOString());
              }
            }
          } else {
            // No preferred status - initialize as online
            if (mounted) {
              setCurrentStatus('online');
              setIsOnline(true);
              setLastSeen(new Date().toISOString());
            }
            await statusService.initializeUser(user.id, true);
          }
        }

        // Set cleanup function for when hook unmounts
        cleanupRef.current = () => statusService.partialCleanup();

      } catch (error) {
        console.error('Error initializing online status:', error);
        setIsTracking(false);
      }
    };

    initializeStatus();

    // Cleanup function
    return () => {
      mounted = false;
      if (statusListenerRef.current) {
        statusListenerRef.current();
        statusListenerRef.current = null;
      }
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      setIsTracking(false);
    };
  }, [user?.id, autoTrack]);

  // Manual status update function
  const updateStatus = useCallback(async (status: UserStatus) => {
    if (!user?.id) return;

    // Mark this as a manual status change to respect user intent
    statusService.setManualStatus(status);

    // Save as preferred status in database
    await statusService.savePreferredStatus(user.id, status);

    // Immediately update local state for better responsiveness
    const previousStatus = currentStatus;
    setCurrentStatus(status);
    setIsOnline(status !== 'offline' && status !== 'invisible');
    setLastSeen(new Date().toISOString());

    try {
      await statusService.updateStatus(status, false, true); // Mark as manual update
    } catch (error) {
      console.error('Error updating status:', error);
      // Revert to previous status on error
      setCurrentStatus(previousStatus);
      setIsOnline(previousStatus !== 'offline' && previousStatus !== 'invisible');
      // Also revert manual status on error
      statusService.setManualStatus(previousStatus);
    }
  }, [user?.id, currentStatus]);

  // Get status of a specific user
  const getUserStatus = useCallback(async (userId: string): Promise<UserStatusInfo | null> => {
    return await statusService.getUserStatus(userId);
  }, []);

  // Get statuses of multiple users
  const getUsersStatuses = useCallback(async (userIds: string[]): Promise<UserStatusInfo[]> => {
    return await statusService.getUsersStatuses(userIds);
  }, []);



  return {
    currentStatus,
    isOnline,
    updateStatus,
    getUserStatus,
    getUsersStatuses,
    lastSeen,
    isTracking
  };
};

// Hook for tracking status of other users
export const useUserStatus = (userId: string) => {
  const [statusInfo, setStatusInfo] = useState<UserStatusInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;

    const fetchStatus = async () => {
      setIsLoading(true);
      try {
        const info = await statusService.getUserStatus(userId);
        setStatusInfo(info);
      } catch (error) {
        console.error('Error fetching user status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatus();

    // Set up real-time listener
    const unsubscribe = statusService.onStatusUpdate(userId, (info) => {
      setStatusInfo(info);
    });

    return unsubscribe;
  }, [userId]);

  return {
    statusInfo,
    isLoading,
    isOnline: statusInfo?.is_online || false,
    status: statusInfo?.status || 'offline',
    lastSeen: statusInfo?.last_seen || null
  };
};

// Hook for tracking multiple users' statuses
export const useUsersStatuses = (userIds: string[]) => {
  const [statuses, setStatuses] = useState<UserStatusInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userIds.length) {
      setStatuses([]);
      setIsLoading(false);
      return;
    }

    const fetchStatuses = async () => {
      setIsLoading(true);
      try {
        const statusList = await statusService.getUsersStatuses(userIds);
        setStatuses(statusList);
      } catch (error) {
        console.error('Error fetching users statuses:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatuses();

    // Set up real-time listeners for all users
    const unsubscribers: (() => void)[] = [];

    userIds.forEach(userId => {
      const unsubscribe = statusService.onStatusUpdate(userId, (info) => {
        setStatuses(prev => {
          const existing = prev.find(s => s.id === info.id);
          if (existing) {
            return prev.map(s => s.id === info.id ? info : s);
          } else {
            return [...prev, info];
          }
        });
      });
      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [userIds]);

  return {
    statuses,
    isLoading
  };
};

